import React from 'react';
import { Text } from '../text';
import { sliderTheme } from '../../styles/themes/slider';

export type SliderProps = {
    id?: string;
    initialValue?: number;
    min?: number;
    max?: number;
    step?: number;
    disabled?: boolean;
    showLabel?: boolean;
    labels?: string[];
    ariaLabel?: string;
    onChange?: (e: number) => void;
    onDrop?: (e: number) => void;
};

const RANGE_OFFSET = 20;

export const Slider = React.forwardRef<HTMLDivElement, SliderProps>((props, ref) => {
    // Props
    const {
        id,
        min = 0,
        max = 100,
        step = 1,
        initialValue,
        showLabel = true,
        disabled,
        labels = [],
        ariaLabel,
        onChange,
        onDrop,
    } = props;

    // Local state
    const [value, setValue] = React.useState(initialValue || min);
    const [isDragging, setIsDragging] = React.useState(false);

    // Variables
    const sliderLabels = labels.length ? labels : [min.toString(), Math.round(max / 2).toString(), max.toString()];
    const thumb = Math.max(((value - min) / (max - min)) * 100, 2);

    const handleMouseDown = () => setIsDragging(true);

    const getMousePosition = (e: React.MouseEvent) => {
        const thumbElement = e?.target as HTMLDivElement;
        const bar = thumbElement?.parentNode as HTMLDivElement;
        const barBox = bar?.getBoundingClientRect();
        const fraction = (e?.clientX - barBox?.left) / barBox?.width;
        return Math.round(min + fraction * (max - min));
    };

    const handleMouseMove = (event: React.MouseEvent) => {
        if (isDragging) {
            const newValue = Math.ceil(Math.max(getMousePosition(event), min) / step) * step;
            if (newValue >= min && newValue <= max) {
                setValue(newValue);
                onChange?.(newValue);
            }
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        onDrop?.(value);
    };

    return (
        <div
            ref={ref}
            id={id}
            data-testid="multi-range-slider"
            className={sliderTheme.base}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            aria-label={ariaLabel}
        >
            <div className="relative w-full">
                <div className="mx-1">
                    <input
                        id="range-value"
                        type="range"
                        step={step}
                        min={min}
                        max={max}
                        onMouseDown={handleMouseDown}
                        value={value}
                        className={sliderTheme.input}
                        style={{ left: `calc(${thumb}% - ${RANGE_OFFSET}px)` }}
                        disabled={disabled}
                        readOnly // required when not using onChange
                    />
                    <div className={sliderTheme.range}>
                        <div className={sliderTheme.bar} />
                        <div className={sliderTheme.innerBar} style={{ left: '0px', right: `${100 - thumb}%` }} />
                        <div
                            className={sliderTheme.handle.base}
                            style={{ left: `calc(${thumb}% - ${RANGE_OFFSET}px)` }}
                        />
                    </div>
                </div>

                {showLabel && sliderLabels.length ? (
                    <div className={sliderTheme.label}>
                        {sliderLabels.map((label) => (
                            <Text key={label} variant="body-small" align="center">
                                {label}
                            </Text>
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
});

Slider.displayName = 'Slider';
